import { Pointer } from "rfc6902";

/**
 * Retrieves a value from an object using an RFC 6901 JSON Pointer path and validates its type.
 *
 * @template T - The expected type of the value
 * @param dataPath - RFC 6901 JSON Pointer path (e.g., "/analysisSets/set1/name")
 * @param source - The source object to retrieve the value from
 * @param isFunction - Type guard function that validates the retrieved value
 * @returns The value if it exists and passes the type guard, otherwise undefined
 */
export const getFromDataPath = <T>(
  dataPath: string,
  source: object,
  isFunction: (obj: unknown) => obj is T,
): T | undefined => {
  const pointer = Pointer.fromJSON(dataPath);
  const value = pointer.get(source);
  if (isFunction(value)) {
    return value;
  }
  return undefined;
};

/**
 * Type guard to check if a value is a string.
 *
 * @param obj - The value to check
 * @returns True if the value is a string, false otherwise
 */
export const isString = (obj: unknown): obj is string => typeof obj === "string";

/**
 * Type guard to check if a value is a number.
 *
 * @param obj - The value to check
 * @returns True if the value is a number, false otherwise
 */
export const isNumber = (obj: unknown): obj is number => typeof obj === "number";

/**
 * Type guard to check if a value is a boolean.
 *
 * @param obj - The value to check
 * @returns True if the value is a boolean, false otherwise
 */
export const isBoolean = (obj: unknown): obj is boolean => typeof obj === "boolean";
